/** @jsxImportSource @emotion/react */
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { Boton, formatFecha } from "../../../shared";
import Flujograma from "../../../elementos/Flujograma";

// Flujograma de una actividad: el flujo real de su tipo, con los pasos que
// están abiertos marcados. El dibujo no cambia con las decisiones: si un paso
// se decidió varias veces (porque la actividad avanzó y volvió), todas sus
// decisiones se listan dentro de la misma caja.

const ANCHO_PASO = 250;

// Altos de cada parte de la caja, para calcular el alto total (las cajas se
// ubican con posición fija, así que el alto no puede salir del contenido)
// Espacio entre el tipo de paso y lo que sigue (los asignados)
const SEPARACION_TIPO = 8;
const ALTO_BASE = 56 + SEPARACION_TIPO; // márgenes, nombre y tipo de paso
const ALTO_ETIQUETA = 26;
const ALTO_LINEA = 19;
const ALTO_EXTRA = 30;
const ALTO_DECISION = 24;
// Línea que separa las decisiones del resto de la caja
const MARGEN_DIVISOR = 4;
const ALTO_DIVISOR = MARGEN_DIVISOR * 2 + 1;
const ALTO_BOTON = 28;

// Filas que ocupan los botones de abajo de la caja: los dos (avance
// administrativo y el propio del paso) no entran en una sola
const filasDeBotones = (avance, propio) => (avance ? 1 : 0) + (propio ? 1 : 0);

const unaLinea = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const nombreDe = (persona) =>
  [persona?.nombre, persona?.apellido].filter(Boolean).join(" ");

/** Decisiones de un paso (de colaboradores y administrativas), por fecha. */
export function decisionesDePaso(paso) {
  return [
    ...(paso.actividadpaso_x_auditor || []).flatMap((asignacion) =>
      (asignacion.actividadpasodecision || []).map((decision) => ({
        decision,
        esAdmin: false,
        auditor: asignacion.auditor,
        nombre: nombreDe(asignacion.auditor),
        fecha: new Date(decision.fechadecision)
      }))
    ),
    ...(paso.actividadpasodecisionadmin || []).map((decision) => ({
      decision,
      esAdmin: true,
      administrador: decision.administrador,
      nombre: nombreDe(decision.administrador),
      fecha: new Date(decision.fechadecision)
    }))
  ].sort((a, b) => a.fecha - b.fecha);
}

/** Personas asignadas a un paso, agrupadas por rol: [[rol, nombres], ...]. */
export function asignadosDePaso(paso) {
  const colaboradores = (paso.actividadpaso_x_auditor || []).filter(
    (a) => a.activo !== false
  );
  const administradores = (paso.actividadpaso_x_administrador || []).map(
    (a) => nombreDe(a.usuario)
  );
  return [
    ["Decisor", colaboradores.filter((a) => a.decisor).map((a) => nombreDe(a.auditor))],
    ["En formación", colaboradores.filter((a) => !a.decisor).map((a) => nombreDe(a.auditor))],
    ["Administrador", administradores]
  ].filter(([, nombres]) => nombres.length > 0);
}

/**
 * @param {object} actividad la actividad, con sus pasos y su `flujo`
 *   ({ pasoInicialId, transiciones }, por id de paso configurado)
 * @param {(paso) => React.ReactNode} [extraDePaso] bloque propio de un paso
 *   (el ensayo, en los pasos de tipo Ensayo); null si el paso no lleva
 * @param {(paso) => React.ReactNode} [botonDePaso] botón propio de un paso,
 *   que va con los botones de abajo (asignar ensayo); null si no lleva
 * @param {(paso, decisiones) => void} onVerDetalles abre el detalle de un paso
 * @param {(paso) => void} onAvanceAdmin abre el avance administrativo de un
 *   paso; el botón solo aparece en los pasos abiertos
 */
export default function FlujoActividad({
  actividad,
  extraDePaso,
  botonDePaso,
  onVerDetalles,
  onAvanceAdmin
}) {
  const finalizada = !!actividad.fechafinalizacion;

  // El flujo es el propio de la actividad, entre sus pasos
  const pasos = (actividad.actividadpaso || [])
    .slice()
    .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
    .map((paso) => {
      const decisiones = decisionesDePaso(paso);
      const asignados = asignadosDePaso(paso);
      const extra = extraDePaso ? extraDePaso(paso) : null;
      const botonPropio = botonDePaso ? botonDePaso(paso) : null;
      // Abierto: es un paso actual de una actividad sin finalizar
      const abierto = paso.actual && !finalizada;
      // Su rama ya terminó y espera a las demás para seguir
      const esperando = paso.ramaCompletada && !finalizada;
      return {
        id: paso.id,
        paso,
        decisiones,
        asignados,
        extra,
        botonPropio,
        abierto,
        esperando,
        alto:
          ALTO_BASE +
          (!abierto && esperando ? ALTO_ETIQUETA : 0) +
          // Una línea por persona asignada
          asignados.reduce((suma, [, nombres]) => suma + nombres.length, 0) *
            ALTO_LINEA +
          (extra ? ALTO_EXTRA : 0) +
          (decisiones.length > 0 ? ALTO_DIVISOR : 0) +
          decisiones.length * ALTO_DECISION +
          filasDeBotones(abierto && onAvanceAdmin, botonPropio) * ALTO_BOTON
      };
    });

  if (pasos.length === 0) {
    return (
      <p css={{ color: "var(--grisTextos)" }}>No hay pasos en esta actividad</p>
    );
  }

  return (
    <Flujograma
      pasos={pasos}
      transiciones={actividad.flujo?.transiciones || []}
      pasoInicialId={actividad.flujo?.pasoInicialId ?? null}
      anchoPaso={ANCHO_PASO}
      altoDePaso={(nodo) => nodo.alto}
      estiloDePaso={(nodo) => ({
        padding: "6px 10px",
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        // Los pasos abiertos van marcados
        ...(nodo.abierto
          ? {
              border: "2px solid var(--amarillo)",
              backgroundColor: "var(--amarilloLight)"
            }
          : {}),
        "& .p-button": { padding: "0.15em 0.3em" }
      })}
      renderPaso={(nodo) => (
        <>
          {/* El nombre va completo, en varias líneas si hace falta */}
          <strong
            css={{
              fontSize: "0.9rem",
              paddingRight: "22px",
              overflowWrap: "anywhere"
            }}
          >
            {nodo.paso.nombre}
          </strong>
          {/* Ver detalles, en la esquina superior derecha */}
          <div
            css={{
              position: "absolute",
              top: "3px",
              right: "3px",
              "& .p-button": { width: "1.6rem", height: "1.6rem", padding: 0 }
            }}
          >
            {/* Sin color propio: Boton lo pinta con el del organismo */}
            <Boton
              icono
              callback={() => onVerDetalles(nodo.paso, nodo.decisiones)}
            >
              <i className="pi pi-plus" />
            </Boton>
          </div>
          <div
            css={{
              color: "var(--grisTextos)",
              marginBottom: `${SEPARACION_TIPO}px`,
              ...unaLinea
            }}
          >
            {nodo.paso.pasoTipo?.nombre || ""}
          </div>

          {/* El paso abierto se distingue por su borde; el que espera a
              otros pasos lleva etiqueta */}
          {!nodo.abierto && nodo.esperando && (
            <div>
              <Tag value="Esperando a otros pasos" severity="info" />
            </div>
          )}

          {/* Una línea por persona; el rol va en la primera de cada grupo y
              las demás quedan alineadas debajo del primer nombre */}
          {nodo.asignados.map(([rol, nombres]) => (
            <div
              key={rol}
              css={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                columnGap: "0.3em",
                color: "var(--grisTextos)"
              }}
            >
              <strong>{rol}:</strong>
              {nombres.map((nombre, i) => (
                <div
                  key={i}
                  css={{ gridColumn: 2, minWidth: 0, ...unaLinea }}
                >
                  {nombre}
                </div>
              ))}
            </div>
          ))}

          {nodo.extra}

          {/* Una línea por cada decisión que se tomó en el paso (fecha y
              resultado; el detalle está en el sidebar), separadas del resto */}
          {nodo.decisiones.length > 0 && (
            <Divider
              css={{ "&.p-divider-horizontal": { margin: `${MARGEN_DIVISOR}px 0` } }}
            />
          )}
          {nodo.decisiones.map((item) => (
            <div
              key={`${item.esAdmin ? "admin" : "colab"}-${item.decision.id}`}
              css={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "6px"
              }}
            >
              <span>{formatFecha(item.decision.fechadecision)}</span>
              <Tag
                value={item.decision.aprobado ? "Aprobado" : "Rechazado"}
                severity={item.decision.aprobado ? "success" : "danger"}
                css={{ fontSize: "0.65rem", padding: "1px 6px", flexShrink: 0 }}
              />
            </div>
          ))}

          {(nodo.abierto && onAvanceAdmin) || nodo.botonPropio ? (
            <div css={{ marginTop: "auto" }}>
              {nodo.abierto && onAvanceAdmin && (
                <div>
                  <Boton texto chico callback={() => onAvanceAdmin(nodo.paso)}>
                    Avance administrativo
                  </Boton>
                </div>
              )}
              {nodo.botonPropio && <div>{nodo.botonPropio}</div>}
            </div>
          ) : null}
        </>
      )}
    />
  );
}
